import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Close,
  NavigateBefore,
  NavigateNext,
  CropFree,
} from "@mui/icons-material";
import ImageCropTool from "./ImageCropTool";
import {
  PoopRecord,
  CONSISTENCY_TYPES,
  SHAPE_TYPES,
  COLOR_TYPES,
  QUANTITY_TYPES,
  HEALTH_TYPES,
  MUCUS_TYPES,
  BLOOD_TYPES,
  FLOATING_TYPES,
  CONDITIONS_TYPES,
  CONDITIONS_FEATURES,
} from "../../types/poop";
import { usePoopCrud } from "../../hooks/usePoopData";
import ToggleButtonField from "../common/ToggleButtonField";

// ML Training options
const ML_TRAINING_OPTIONS = {
  "": "Not Set",
  true: "✅ Yes",
  false: "❌ No",
};

// Simplified Bristol Type labels for toggle buttons
const BRISTOL_TYPES_SHORT = {
  1: "Type 1",
  2: "Type 2",
  3: "Type 3",
  4: "Type 4",
  5: "Type 5",
  6: "Type 6",
  7: "Type 7",
} as const;

// What a save writes: the fields this form edits. Queue rows also carry
// columns the backend computes (has_consent), and echoing one back fails the
// whole UPDATE. skipped rides along so deciding on a photo after NOT NOW
// clears the skip.
const SAVED_FIELDS: readonly (keyof PoopRecord)[] = [
  "image_good_for_ml",
  "bristol_type",
  "shape",
  "quantity",
  "color",
  "blood",
  "mucus",
  "floating",
  "consistency",
  "health",
  "skipped",
  ...CONDITIONS_FEATURES,
];

const pickSavedFields = (data: Partial<PoopRecord>): Partial<PoopRecord> => {
  const picked: Partial<PoopRecord> = {};
  SAVED_FIELDS.forEach(<K extends keyof PoopRecord>(field: K) => {
    if (data[field] !== undefined) picked[field] = data[field];
  });
  return picked;
};

interface FastPhotoEditorProps {
  open: boolean;
  records: PoopRecord[];
  initialIndex: number;
  onClose: () => void;
  onUpdate: () => void;
  // The options below let Data Samples review use this same editor; AI
  // Review passes none of them and behaves as it always has.
  /** Saves one photo's edits. Default: the AI Review save (PUT /poop/:id). */
  saveRecord?: (
    record: PoopRecord,
    data: Partial<PoopRecord>
  ) => Promise<PoopRecord | null>;
  /** The crop tool replaces the app.poop image. Default: shown. */
  allowCrop?: boolean;
  /** Extra chips beside the photo counter, e.g. the sample id. */
  renderBadges?: (record: PoopRecord) => React.ReactNode;
}

const FastPhotoEditor: React.FC<FastPhotoEditorProps> = ({
  open,
  records,
  initialIndex,
  onClose,
  onUpdate,
  saveRecord,
  allowCrop = true,
  renderBadges,
}) => {
  const { updateRecord } = usePoopCrud();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [formData, setFormData] = useState<Partial<PoopRecord>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [failedSaves, setFailedSaves] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const currentRecordIdRef = useRef<string | null>(null);
  const currentRecordRef = useRef<PoopRecord | null>(null);
  const pendingSavesRef = useRef(new Set<Promise<void>>());

  const currentRecord = records[currentIndex];

  // Initialize form data when current record changes
  useEffect(() => {
    if (currentRecord) {
      const initializedData = {
        ...currentRecord,
        bristol_type: currentRecord.bristol_type ?? 1,
        consistency: currentRecord.consistency ?? 0,
        shape: currentRecord.shape ?? 0,
        quantity: currentRecord.quantity ?? 0,
        color: currentRecord.color ?? 0,
        health: currentRecord.health ?? 0,
        blood: currentRecord.blood ?? 0,
        mucus: currentRecord.mucus ?? 0,
        floating: currentRecord.floating ?? 0,
        image_good_for_ml: currentRecord.image_good_for_ml ?? null,
        liver_flukes: currentRecord.liver_flukes ?? 0,
        colon_cancer: currentRecord.colon_cancer ?? 0,
        hemorrhoids: currentRecord.hemorrhoids ?? 0,
        anal_fissures: currentRecord.anal_fissures ?? 0,
        crohns_disease: currentRecord.crohns_disease ?? 0,
        ulcerative_colitis: currentRecord.ulcerative_colitis ?? 0,
        celiac_disease: currentRecord.celiac_disease ?? 0,
        gallbladder_disease: currentRecord.gallbladder_disease ?? 0,
        pancreatitis: currentRecord.pancreatitis ?? 0,
        liver_disease: currentRecord.liver_disease ?? 0,
        upper_gastrointestinal_bleeding:
          currentRecord.upper_gastrointestinal_bleeding ?? 0,
        gastrointestinal_infection:
          currentRecord.gastrointestinal_infection ?? 0,
        lactose_intolerance: currentRecord.lactose_intolerance ?? 0,
        food_poisoning: currentRecord.food_poisoning ?? 0,
        diverticulitis: currentRecord.diverticulitis ?? 0,
        irritable_bowel_syndrome: currentRecord.irritable_bowel_syndrome ?? 0,
        constipation: currentRecord.constipation ?? 0,
        dehydration: currentRecord.dehydration ?? 0,
        hypothyroidism: currentRecord.hypothyroidism ?? 0,
        bile_duct_obstruction: currentRecord.bile_duct_obstruction ?? 0,
        malabsorption_syndrome: currentRecord.malabsorption_syndrome ?? 0,
        rapid_gastrointestinal_transit:
          currentRecord.rapid_gastrointestinal_transit ?? 0,
      };

      setFormData(initializedData);
      setHasChanges(false);
      currentRecordIdRef.current = currentRecord.id;
      currentRecordRef.current = currentRecord;

      // Scroll to top when changing records
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }
  }, [currentRecord]);

  // Reset index when opening
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const handleInputChange = (field: keyof PoopRecord, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  // Saves don't block moving on, but they're tracked: closing waits for them,
  // and a failed one is counted on screen since its photo stays in the queue.
  // updateRecord resolves null on failure; the tracked promise never rejects.
  const trackSave = useCallback((save: Promise<PoopRecord | null>) => {
    const pending = save.then(
      (saved) => {
        if (!saved) setFailedSaves((count) => count + 1);
      },
      () => setFailedSaves((count) => count + 1)
    );
    pendingSavesRef.current.add(pending);
    pending.then(() => pendingSavesRef.current.delete(pending));
  }, []);

  // Fire and forget save - don't wait for response
  const saveInBackground = useCallback(() => {
    if (!currentRecordIdRef.current || !hasChanges) return;

    // Fire the request and don't wait
    const fields = pickSavedFields(formData);
    trackSave(
      saveRecord && currentRecordRef.current
        ? saveRecord(currentRecordRef.current, fields)
        : updateRecord(currentRecordIdRef.current, fields)
    );

    setHasChanges(false);
  }, [hasChanges, formData, updateRecord, saveRecord, trackSave]);

  const goToNext = useCallback(() => {
    if (currentIndex < records.length - 1) {
      // Save current changes in background
      if (hasChanges) {
        saveInBackground();
      }
      // Immediately move to next
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, records.length, hasChanges, saveInBackground]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      // Save current changes in background
      if (hasChanges) {
        saveInBackground();
      }
      // Immediately move to previous
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex, hasChanges, saveInBackground]);

  const handleClose = useCallback(() => {
    // Save before closing if there are changes
    if (hasChanges) {
      saveInBackground();
    }
    onClose();
    // Reload the queue once the saves land; reloading right away brings back
    // the photos whose saves are still in flight.
    Promise.all(Array.from(pendingSavesRef.current)).then(() => onUpdate());
  }, [hasChanges, saveInBackground, onUpdate, onClose]);

  // NOT NOW defers the good-for-ML decision, never the corrections: the
  // labels are saved as shown, the decision stays as it was, and the photo is
  // marked skipped so AI Review doesn't offer it again. With a custom save
  // (Data Samples) it always saves: the photo was checked, ships with these
  // labels, and stays out of Ready to Train.
  const handleSkip = useCallback(() => {
    const record = currentRecordRef.current;
    if (!currentRecordIdRef.current || !record) return;

    const fields: Partial<PoopRecord> =
      hasChanges || saveRecord
        ? {
            ...pickSavedFields(formData),
            image_good_for_ml: record.image_good_for_ml ?? null,
            skipped: true,
          }
        : { skipped: true };
    trackSave(
      saveRecord
        ? saveRecord(record, fields)
        : updateRecord(currentRecordIdRef.current, fields)
    );
    setHasChanges(false);

    // Move to next image
    if (currentIndex < records.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [
    currentIndex,
    records.length,
    updateRecord,
    saveRecord,
    trackSave,
    hasChanges,
    formData,
  ]);

  if (!open || !currentRecord) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: "env(safe-area-inset-top)",
        left: 0,
        right: 0,
        bottom: "env(safe-area-inset-bottom)",
        bgcolor: "background.default",
        zIndex: 1300,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, minWidth: 0 }}>
          <IconButton onClick={handleClose} size="small" aria-label="Close">
            <Close />
          </IconButton>
          <Typography variant="h6" fontWeight="bold" noWrap>
            {currentIndex + 1} / {records.length}
          </Typography>
          <Typography variant="body2" mx={1} fontWeight={"bold"} noWrap>
            Type {records[currentIndex]?.bristol_type}
          </Typography>
        </Box>

        {/* On a phone the words go, the arrows stay: the row must fit 360px. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            aria-label="Previous"
            sx={{ minWidth: { xs: 44, sm: 64 }, "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 } } }}
            startIcon={<NavigateBefore />}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Previous
            </Box>
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={goToNext}
            disabled={currentIndex === records.length - 1}
            aria-label="Next"
            sx={{ minWidth: { xs: 44, sm: 64 }, "& .MuiButton-endIcon": { ml: { xs: 0, sm: 1 } } }}
            endIcon={<NavigateNext />}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Next
            </Box>
          </Button>
        </Box>
      </Box>

      {/* Main content - scrollable */}
      <Box
        ref={contentRef}
        sx={{
          height: "calc(100% - 73px)",
          overflowY: "auto",
          // Don't pass the scroll on to the page behind at either end.
          overscrollBehavior: "contain",
        }}
      >
        {/* Image */}
        {formData.s3_url && (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "300px",
              bgcolor: "black",
            }}
          >
            {renderBadges && (
              <Box sx={{ position: "absolute", top: 8, left: 8, right: 8, zIndex: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {renderBadges(currentRecord)}
              </Box>
            )}
            <img
              src={formData.s3_url}
              alt="Record"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
            {allowCrop && <Button
              size="small"
              variant="contained"
              startIcon={<CropFree />}
              onClick={() => setCropOpen(true)}
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                bgcolor: "rgba(0,0,0,0.65)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.85)" },
                fontSize: "0.7rem",
              }}
            >
              Crop
            </Button>}
          </Box>
        )}

        {/* Crop & Analyze Dialog */}
        <Dialog
          open={cropOpen}
          onClose={() => setCropOpen(false)}
          maxWidth="md"
          fullWidth
          sx={{
            "& .MuiDialog-container": {
              pt: "env(safe-area-inset-top)",
              pb: "env(safe-area-inset-bottom)",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Crop image
            <IconButton size="small" onClick={() => setCropOpen(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {formData.s3_url && currentRecord && (
              <ImageCropTool
                recordId={currentRecord.id}
                onSaved={(updated) => {
                  setFormData((prev) => {
                    const next: Partial<PoopRecord> = { ...prev };
                    if (updated.s3_url !== undefined) next.s3_url = updated.s3_url;
                    if (updated.s3_key !== undefined) next.s3_key = updated.s3_key;
                    return next;
                  });
                  setCropOpen(false);
                  // No list reload here: it would unmount the editor
                  // mid-review. Closing the editor reloads the list.
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Form */}
        <Box sx={{ p: 3, pb: 20 }}>
          {/* ML Training Flag */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              color="primary"
              sx={{ mb: 2 }}
            >
              ML Training
            </Typography>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <ToggleButtonField
                  label="Is this image good for ML training?"
                  value={
                    formData.image_good_for_ml === null
                      ? ""
                      : formData.image_good_for_ml
                        ? "true"
                        : "false"
                  }
                  options={ML_TRAINING_OPTIONS}
                  onChange={(value) => {
                    const strValue = value.toString();
                    let newValue: boolean | null = null;
                    if (strValue === "true") newValue = true;
                    else if (strValue === "false") newValue = false;
                    handleInputChange("image_good_for_ml", newValue);
                  }}
                  fullWidth
                />
              </Box>
              <Button
                variant="contained"
                onClick={handleSkip}
                disabled={currentIndex === records.length - 1}
                sx={{
                  bgcolor: "error.main",
                  "&:hover": { bgcolor: "error.dark" },
                  minWidth: "120px",
                  mt: 3,
                }}
              >
                NOT NOW
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonField
                label="Bristol Type"
                value={formData.bristol_type}
                options={BRISTOL_TYPES_SHORT}
                onChange={(value) =>
                  handleInputChange("bristol_type", Number(value))
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonField
                label="Shape"
                value={formData.shape}
                options={SHAPE_TYPES}
                onChange={(value) => handleInputChange("shape", Number(value))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonField
                label="Quantity"
                value={formData.quantity}
                options={QUANTITY_TYPES}
                onChange={(value) =>
                  handleInputChange("quantity", Number(value))
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonField
                label="Color"
                value={formData.color}
                options={COLOR_TYPES}
                onChange={(value) => handleInputChange("color", Number(value))}
                fullWidth
              />
            </Grid>

            {/* Additional Details */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Additional Details
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonField
                label="Blood"
                value={formData.blood}
                options={BLOOD_TYPES}
                onChange={(value) => handleInputChange("blood", Number(value))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonField
                label="Mucus"
                value={formData.mucus}
                options={MUCUS_TYPES}
                onChange={(value) => handleInputChange("mucus", Number(value))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonField
                label="Floating"
                value={formData.floating}
                options={FLOATING_TYPES}
                onChange={(value) =>
                  handleInputChange("floating", Number(value))
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonField
                label="Consistency"
                value={formData.consistency}
                options={CONSISTENCY_TYPES}
                onChange={(value) =>
                  handleInputChange("consistency", Number(value))
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonField
                label="Health"
                value={formData.health}
                options={HEALTH_TYPES}
                onChange={(value) => handleInputChange("health", Number(value))}
                fullWidth
              />
            </Grid>

            {/* Medical Conditions */}
            <Grid item xs={12} sx={{ mt: 2 }} display={"none"}>
              <Typography variant="h6" gutterBottom>
                Medical Conditions
              </Typography>
            </Grid>

            <Grid item xs={12} display={"none"}>
              <Grid container spacing={3}>
                {CONDITIONS_FEATURES.map((condition) => (
                  <Grid item xs={12} key={condition}>
                    <ToggleButtonField
                      label={condition
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                      value={
                        formData[condition as keyof PoopRecord] as
                          | number
                          | undefined
                      }
                      options={CONDITIONS_TYPES}
                      onChange={(value) =>
                        handleInputChange(
                          condition as keyof PoopRecord,
                          Number(value),
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Snackbar
        open={failedSaves > 0}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setFailedSaves(0)}
          sx={{ width: "100%" }}
        >
          {failedSaves === 1
            ? "1 review didn't save. That photo will come back in the queue."
            : `${failedSaves} reviews didn't save. Those photos will come back in the queue.`}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FastPhotoEditor;
